import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import type { DashboardRange, ObligationState } from "@/api";

/**
 * Stable string form of a range, used as the cache key. A range is an object,
 * and react-query hashes objects structurally — but serialising keeps the key
 * readable in devtools and immune to key-order changes.
 */
export const rangeKey = (range: DashboardRange): string => {
	if (range.kind === "allTime") return "all";
	if (range.kind === "preset") return `p${range.days}`;
	return `c${range.from}_${range.to}`;
};

export const dashboardKeys = {
	overview: (range: DashboardRange) => ["dashboard", "overview", rangeKey(range)] as const,
	finances: (range: DashboardRange) => ["dashboard", "finances", rangeKey(range)] as const,
};

export function useDashboardOverview(range: DashboardRange) {
	return useQuery({
		queryKey: dashboardKeys.overview(range),
		queryFn: () => api.dashboard.overview(range),
	});
}

export function useFinanceSummary(range: DashboardRange) {
	return useQuery({
		queryKey: dashboardKeys.finances(range),
		queryFn: () => api.finances.summary(range),
	});
}

/**
 * Действия над обязательством — то, ради чего финансовый экран перестаёт быть
 * отчётом (правка 27): «финдашборд должен помогать делать выплаты».
 *
 * Оптимистично ничего не обновляем: стадию проверяет сервер (шаг назад он
 * отклоняет), и показать «Оплачено» до подтверждения значило бы соврать про
 * деньги. Перечитываем сводку после ответа.
 */
export function useAdvanceObligation(range: DashboardRange) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, state }: { id: string; state: ObligationState }) =>
			api.finances.advanceObligation(id, state),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: dashboardKeys.finances(range) }),
	});
}

export function useGenerateObligationDocuments(range: DashboardRange) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.finances.generateDocuments(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: dashboardKeys.finances(range) }),
	});
}
